import { PAGES } from "@dune/types";
import type { SubmissionParticipantWithValidDecennialCertificate } from "@dune/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useUpdateReceptionParticipantActivitiesAndFees } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { useGetConstructionSiteReceptionParticipants } from "../../../../api/queries/constructionSiteSubmission.queries";
import { getDocumentUrl } from "../../../../api/queries/files.queries";
import { MaterialToSubmissionForm } from "../../../../component/constructionSites/submission/participants/MaterialToSubmissionForm";
import PageLayoutWithTabs from "../../../../component/layout/PageLayoutWithTabs";
import { useDocumentSelection } from "../../../../hooks/panel-sync/useDocumentSelection";
import { useMaterialToSubmissionForm } from "../../../../hooks/useParticipantToSubmissionForm";
import { useDocumentStore } from "../../../../stores/document.store";
import { ParticipantDocumentsReceptionTable } from "../../components/intervenants/ParticipantDocumentsReceptionTable";

export const EditMaterialReceptionPage = () => {
	const navigate = useNavigate();
	const { constructionSiteId, participantId } = useParams<{
		constructionSiteId: string;
		participantId: string;
	}>();
	const [searchParams] = useSearchParams();
	const returnUrl = searchParams.get("returnUrl");

	const numericConstructionSiteId = constructionSiteId
		? Number(constructionSiteId)
		: 0;
	const numericParticipantId = participantId ? Number(participantId) : 0;

	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
		null,
	);

	const { data: receptionParticipants } =
		useGetConstructionSiteReceptionParticipants(numericConstructionSiteId);

	useDocumentSelection({
		entityType: "CONSTRUCTION_SITE",
		entityId: numericConstructionSiteId,
	});

	const enriched = useMemo(() => {
		if (!receptionParticipants || !participantId) return null;
		const rp = receptionParticipants.find(
			(p) => p.participant.id === Number(participantId),
		);
		if (!rp) return null;
		const obj: SubmissionParticipantWithValidDecennialCertificate = {
			id: rp.id,
			type: rp.type,
			designerType: rp.designerType ?? null,
			totalFeesHT: rp.totalFeesHT ?? 0,
			totalFeesTTC: rp.totalFeesTTC ?? 0,
			totalMissionsOrActivitiesOrMaterialTypes:
				rp.totalMissionsOrActivitiesOrMaterialTypes ?? [],
			participant: {
				...rp.participant,
				isNewForReception: !rp.isFromSubmission,
			} as SubmissionParticipantWithValidDecennialCertificate["participant"],
			decennialCertificatesOfSubmissionOrValidFromOthers: null,
			insurer: null,
		};
		return obj;
	}, [receptionParticipants, participantId]);

	const handleDocumentClick = useCallback(
		async (file: {
			id: string;
			key: string;
			bucket: string;
			mimeType: string;
			name: string;
		}) => {
			const docId = file.id;
			if (loadingDocumentId === docId) return;
			if (!file.key || file.key.trim() === "") return;

			setLoadingDocumentId(docId);
			try {
				const url = await getDocumentUrl({
					bucket: file.bucket,
					key: file.key,
				});
				if (url) {
					const selectDocument = useDocumentStore.getState().selectDocument;
					selectDocument(url, docId, file.mimeType, file.name);
				}
			} finally {
				setLoadingDocumentId(null);
			}
		},
		[loadingDocumentId],
	);

	const updateReceptionActivitiesAndFees =
		useUpdateReceptionParticipantActivitiesAndFees(
			numericConstructionSiteId,
			numericParticipantId,
		);

	const formApi = useMaterialToSubmissionForm({
		mode: "update",
		onSubmit: (data) => {
			updateReceptionActivitiesAndFees.mutate(
				{
					totalMissionsOrActivitiesOrMaterialTypes: data.materialTypes || [],
					totalFeesTTC: Number(data.totalFeesTTC) || 0,
					totalFeesHT:
						typeof data.totalFeesHT === "number" ? data.totalFeesHT : null,
				},
				{ onSuccess: () => goBack() },
			);
		},
		constructionSiteId: numericConstructionSiteId,
		participantId: numericParticipantId,
		prefillFrom: enriched,
	});

	useEffect(() => {
		if (enriched) {
			formApi.form.setValue("totalFeesTTC", enriched.totalFeesTTC ?? 0);
			formApi.form.setValue("totalFeesHT", enriched.totalFeesHT ?? 0);
		}
	}, [enriched, formApi.form]);

	function goBack() {
		if (returnUrl) navigate(returnUrl);
		else if (constructionSiteId)
			navigate(PAGES.PROJECTS.reception.withParams(constructionSiteId, 3));
		else navigate(PAGES.PROJECTS.index);
	}

	if (!constructionSiteId || !participantId || !enriched) return null;

	return (
		<PageLayoutWithTabs title="Modifier un matériau">
			<FormProvider {...formApi.form}>
				<form onSubmit={formApi.handleSubmit}>
					<MaterialToSubmissionForm
						constructionSiteId={numericConstructionSiteId}
						onSubmit={formApi.handleSubmit}
						onCancel={goBack}
						isSubmitting={updateReceptionActivitiesAndFees.isPending}
						documentsSlot={
							<ParticipantDocumentsReceptionTable
								submissionParticipant={enriched}
								constructionSiteId={numericConstructionSiteId}
								onDocumentClick={handleDocumentClick}
								loadingDocumentId={loadingDocumentId}
							/>
						}
					/>
				</form>
			</FormProvider>
		</PageLayoutWithTabs>
	);
};

export default EditMaterialReceptionPage;
